<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-app-screen">

    <!-- Mobile skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center px-4 h-14">
            <div class="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
        <div class="pb-20 px-4 pt-6 space-y-6">
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden p-6 flex flex-col items-center gap-4">
            <div class="w-40 h-40 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
            <div class="h-9 w-32 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
          </div>
          <div class="space-y-3">
            <div class="h-2.5 w-28 bg-gray-200 dark:bg-gray-700 rounded mx-1"></div>
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
              <div v-for="i in 3" :key="i" class="px-4 py-3.5">
                <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mx-1"></div>
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden p-4">
              <div class="h-28 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile loaded content -->
    <template v-else>
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center px-4 h-14">
          <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">QR Code</h1>
        </div>
      </div>

      <div class="pb-20 pt-6 px-4 space-y-6">

        <!-- QR preview -->
        <div v-if="qrCodeLink" class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden p-6 flex flex-col items-center gap-4">
          <div class="rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100">
            <img v-if="qrPreviewSrc" :src="qrPreviewSrc" :class="qrCodeDimensions" alt="QR Code">
            <div v-else :class="qrCodeDimensions" class="animate-pulse bg-gray-100 dark:bg-gray-800"></div>
          </div>

          <!-- Download dropdown -->
          <div class="relative w-full">
            <div v-if="downloadOpen" @click="downloadOpen = false" class="fixed inset-0 z-10"></div>
            <button @click="downloadOpen = !downloadOpen"
                    class="relative z-20 w-full flex items-center justify-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-full transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/>
              </svg>
              {{ I18N.website_content.account_settings.qr_code.download_qr_code }}
              <svg :class="{ 'rotate-180': downloadOpen }" class="w-4 h-4 ml-auto transition-transform duration-150" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
              </svg>
            </button>
            <div v-if="downloadOpen"
                 class="absolute bottom-full left-0 right-0 mb-1.5 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden z-20">
              <button @click="downloadAs('png')"
                      class="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <span class="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m4 16 4-4 4 4 4-6 4 6M4 20h16"/>
                  </svg>
                </span>
                <div class="text-left">
                  <p class="font-medium leading-none">PNG</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Raster image, best for sharing</p>
                </div>
              </button>
              <button @click="downloadAs('svg')"
                      class="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-t border-gray-100 dark:border-gray-700">
                <span class="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"/>
                  </svg>
                </span>
                <div class="text-left">
                  <p class="font-medium leading-none">SVG</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Vector, scales to any size</p>
                </div>
              </button>
            </div>
          </div>
        </div>
        <div v-else class="bg-white dark:bg-[#1C2128] rounded-2xl p-8 text-center text-sm text-gray-400 dark:text-gray-500">
          {{ I18N.website_content.account_settings.dev_console_tab.no_qr_code }}
        </div>

        <!-- Configuration -->
        <div>
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">Configuration</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            <!-- Size -->
            <div class="px-4 py-3 dark:text-gray-400">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
                {{ I18N.website_content.account_settings.qr_code.qr_code_size }}
              </p>
              <TimelydoDropdown
                title=""
                name="qrSize"
                :selected_value="selectedSize"
                :values="useSettingsStoreObj().qrcode_size_list"
                :opts="{}"
                @change="selectSize"
              />
            </div>
            <!-- Colour -->
            <div class="px-4 py-3">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Colour</p>
              <div class="flex items-center gap-2 flex-wrap">
                <button v-for="swatch in colorSwatches" :key="swatch.value"
                        @click="selectSwatch(swatch.value)"
                        :style="{ backgroundColor: swatch.value }"
                        :title="swatch.label"
                        class="w-8 h-8 rounded-lg transition-all hover:scale-110 flex-shrink-0 focus:outline-none"
                        :class="qrColor === swatch.value ? 'ring-2 ring-offset-2 ring-gray-500 dark:ring-gray-400 dark:ring-offset-[#1C2128] scale-110' : ''">
                </button>
                <!-- Custom colour input -->
                <label class="relative w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer flex-shrink-0 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500"
                       :style="!isPresetColor ? { backgroundColor: qrColor } : {}"
                       :class="[
                         !isPresetColor ? 'ring-2 ring-offset-2 ring-gray-500 dark:ring-gray-400 dark:ring-offset-[#1C2128] scale-110' : 'border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400'
                       ]"
                       title="Custom colour">
                  <svg v-if="isPresetColor" class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232a3 3 0 0 1 4.243 4.243L8.5 20.45l-5 1 1-5 10.732-10.218Z"/>
                  </svg>
                  <input type="color" :value="qrColor" @input="onCustomColor($event.target.value)"
                         class="absolute inset-0 opacity-0 cursor-pointer w-full h-full">
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Embed code -->
        <div>
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">Embed code</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden p-4 relative">
            <TimelydoTextArea :value="embedCode"
                              rows="5"
                              :placeholder="I18N.website_content.account_settings.qr_code.embed_qr_code"
                              name="user[qr code]"
                              :opts="{id: 'qr_code_text_mobile', disabled: true}" />
            <button @click="copyEmbeddedCode"
                    class="absolute top-5 right-5 flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs font-medium hover:underline">
              {{ I18N.website_content.account_settings.qr_code.copy }}
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"/>
              </svg>
            </button>
          </div>
        </div>

      </div>
    </template>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
          <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-28 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6">
          <div class="flex flex-col lg:flex-row gap-6">
            <div class="flex-1 space-y-4 min-w-0">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                  <div class="h-3.5 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div class="px-6 py-4 space-y-4">
                  <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                  <div class="flex gap-2">
                    <div v-for="i in 8" :key="i" class="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>
                </div>
              </div>
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                  <div class="h-3.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div class="px-6 py-4">
                  <div class="h-28 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                </div>
              </div>
            </div>
            <div class="lg:w-64 flex-shrink-0">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                  <div class="h-3.5 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div class="px-6 py-5 flex flex-col items-center gap-4">
                  <div class="w-40 h-40 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                  <div class="h-9 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Desktop loaded content -->
    <template v-else>
      <!-- Sub-page header -->
      <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
          <div class="flex items-center gap-2.5">
            <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                <rect x="14" y="14" width="3" height="3"/><rect x="18" y="14" width="3" height="3"/><rect x="14" y="18" width="3" height="3"/><rect x="18" y="18" width="3" height="3"/>
              </svg>
            </div>
            <div>
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">QR Code</h1>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Developers Console</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="max-w-5xl mx-auto px-6 py-6">
        <div v-if="qrCodeLink" class="flex flex-col lg:flex-row gap-6">

          <!-- Left column: Configuration + Embed code -->
          <div class="flex-1 space-y-4 min-w-0">

            <!-- Configuration card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Configuration</h2>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">QR code size and colour</p>
                </div>
              </div>
              <div class="px-6 py-4 space-y-4">
                <!-- Size -->
                <div>
                  <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">
                    {{ I18N.website_content.account_settings.qr_code.qr_code_size }}
                  </label>
                  <TimelydoDropdown
                    title=""
                    name="qrSize"
                    :selected_value="selectedSize"
                    :values="useSettingsStoreObj().qrcode_size_list"
                    :opts="{}"
                    @change="selectSize"
                  />
                </div>
                <!-- Colour -->
                <div>
                  <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Colour</label>
                  <div class="flex items-center gap-2 flex-wrap">
                    <button v-for="swatch in colorSwatches" :key="swatch.value"
                            @click="selectSwatch(swatch.value)"
                            :style="{ backgroundColor: swatch.value }"
                            :title="swatch.label"
                            class="w-8 h-8 rounded-lg transition-all hover:scale-110 flex-shrink-0 focus:outline-none"
                            :class="qrColor === swatch.value ? 'ring-2 ring-offset-2 ring-gray-500 dark:ring-gray-400 dark:ring-offset-[#1C2128] scale-110' : ''">
                    </button>
                    <!-- Custom colour input -->
                    <label class="relative w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer flex-shrink-0 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500"
                           :style="!isPresetColor ? { backgroundColor: qrColor } : {}"
                           :class="[
                             !isPresetColor ? 'ring-2 ring-offset-2 ring-gray-500 dark:ring-gray-400 dark:ring-offset-[#1C2128] scale-110' : 'border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400'
                           ]"
                           title="Custom colour">
                      <svg v-if="isPresetColor" class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232a3 3 0 0 1 4.243 4.243L8.5 20.45l-5 1 1-5 10.732-10.218Z"/>
                      </svg>
                      <input type="color" :value="qrColor" @input="onCustomColor($event.target.value)"
                             class="absolute inset-0 opacity-0 cursor-pointer w-full h-full">
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Embed code card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Embed code</h2>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Paste into any webpage to display your QR code</p>
                  </div>
                </div>
                <button @click="copyEmbeddedCode"
                        class="flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
                  {{ I18N.website_content.account_settings.qr_code.copy }}
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"/>
                  </svg>
                </button>
              </div>
              <div class="px-6 py-4">
                <TimelydoTextArea :value="embedCode"
                                  rows="5"
                                  :placeholder="I18N.website_content.account_settings.qr_code.embed_qr_code"
                                  name="user[qr code]"
                                  :opts="{id: 'qr_code_text', disabled: true}" />
              </div>
            </div>

          </div>

          <!-- Right column: QR Code preview -->
          <div class="lg:w-64 flex-shrink-0">
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Preview</h2>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Your shareable profile QR code</p>
                </div>
              </div>
              <div class="px-6 py-5 flex flex-col items-center gap-4">
                <div class="rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100 dark:border-gray-200">
                  <img v-if="qrPreviewSrc" :src="qrPreviewSrc" :class="qrCodeDimensions" alt="QR Code">
                  <div v-else :class="qrCodeDimensions" class="animate-pulse bg-gray-100"></div>
                </div>

                <!-- Download dropdown -->
                <div class="relative w-full">
                  <div v-if="downloadOpen" @click="downloadOpen = false" class="fixed inset-0 z-10"></div>
                  <button @click="downloadOpen = !downloadOpen"
                          class="relative z-20 w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                    </svg>
                    {{ I18N.website_content.account_settings.qr_code.download_qr_code }}
                    <svg :class="{ 'rotate-180': downloadOpen }" class="w-3.5 h-3.5 ml-auto transition-transform duration-150" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
                    </svg>
                  </button>
                  <div v-if="downloadOpen"
                       class="absolute bottom-full left-0 right-0 mb-1.5 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden z-20">
                    <button @click="downloadAs('png')"
                            class="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <span class="w-7 h-7 rounded-md bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                        <svg class="w-3.5 h-3.5 text-orange-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="m4 16 4-4 4 4 4-6 4 6M4 20h16"/>
                        </svg>
                      </span>
                      <div class="text-left">
                        <p class="font-medium text-xs leading-none">PNG</p>
                        <p class="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">Best for sharing</p>
                      </div>
                    </button>
                    <button @click="downloadAs('svg')"
                            class="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-t border-gray-100 dark:border-gray-700">
                      <span class="w-7 h-7 rounded-md bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                        <svg class="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"/>
                        </svg>
                      </span>
                      <div class="text-left">
                        <p class="font-medium text-xs leading-none">SVG</p>
                        <p class="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">Scales to any size</p>
                      </div>
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>

        <!-- No QR code state -->
        <div v-else class="flex flex-col items-center justify-center py-32 text-center px-4">
          <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ I18N.website_content.account_settings.dev_console_tab.no_qr_code }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import QRCodeStyling from 'qr-code-styling';
import {useSettingsStore} from 'controllers/vue_components/settings/v1/store'
import RequestMixin from "controllers/vue_components/mixins/request"
import Constants from "controllers/vue_components/mixins/constants"
import FlashNotifications from "controllers/vue_components/flash_toast"
import Formatting from "controllers/vue_components/mixins/formatting";
import TimelydoDropdown from "controllers/vue_components/fields/timelydo_dropdown"
import TimelydoTextArea from "controllers/vue_components/fields/timelydo_textarea"

const DOWNLOAD_SIZE_MAP = { small: 256, medium: 512, large: 1024 };

const COLOR_SWATCHES = [
    { label: 'Blue',   value: '#2563eb', dark: '#1d4ed8' },
    { label: 'Indigo', value: '#4f46e5', dark: '#4338ca' },
    { label: 'Purple', value: '#7c3aed', dark: '#6d28d9' },
    { label: 'Pink',   value: '#db2777', dark: '#be185d' },
    { label: 'Red',    value: '#dc2626', dark: '#b91c1c' },
    { label: 'Teal',   value: '#0d9488', dark: '#0f766e' },
    { label: 'Green',  value: '#16a34a', dark: '#15803d' },
    { label: 'Black',  value: '#111827', dark: '#000000' },
];

export default {
    name: 'DevelopersQrCode',
    props: [],
    mixins: [RequestMixin, Constants, Formatting],
    components: {
        FlashNotifications, TimelydoDropdown, TimelydoTextArea
    },
    data() {
        return {
            qrPreviewSrc: null,
            selectedSize: 'medium',
            qrColor: '#2563eb',
            downloadOpen: false,
            colorSwatches: COLOR_SWATCHES,
            qrColorDebounce: null,
        };
    },
    methods: {
        useSettingsStoreObj() {
            return useSettingsStore();
        },

        selectSize(e) { this.selectedSize = e.qrSize; },

        selectSwatch(color) {
            this.qrColor = color;
            this.generateQRCode();
        },

        onCustomColor(color) {
            this.qrColor = color;
            clearTimeout(this.qrColorDebounce);
            this.qrColorDebounce = setTimeout(() => this.generateQRCode(), 250);
        },

        copyEmbeddedCode() {
            this.copyTextToClipboard(this.embedCode);
            this.$refs.flash.show(this.I18N.website_content.account_settings.code_copied, 'notice');
        },

        getQrOptions(size, type = 'canvas', imageMargin = 6) {
            return {
                type,
                width: size,
                height: size,
                data: this.qrCodeLink,
                image: '/images/brand_icon.svg',
                qrOptions: { errorCorrectionLevel: 'H' },
                imageOptions: {
                    crossOrigin: 'anonymous',
                    imageSize: 0.3,
                    margin: imageMargin,
                    hideBackgroundDots: true,
                },
                dotsOptions: {
                    color: this.qrColor,
                    type: 'rounded',
                },
                backgroundOptions: {
                    color: '#ffffff',
                },
                cornersSquareOptions: {
                    type: 'extra-rounded',
                    color: this.qrCornerColor,
                },
                cornersDotOptions: {
                    type: 'dot',
                    color: this.qrCornerColor,
                },
            };
        },

        generateQRCode() {
            if (!this.qrCodeLink) return;
            const qr = new QRCodeStyling(this.getQrOptions(400));
            qr.getRawData('png')
                .then((blob) => {
                    const reader = new FileReader();
                    reader.onloadend = () => { this.qrPreviewSrc = reader.result; };
                    reader.readAsDataURL(blob);
                })
                .catch((err) => { console.error('QR generation failed:', err); });
        },

        downloadAs(fmt) {
            this.downloadOpen = false;
            const size = DOWNLOAD_SIZE_MAP[this.selectedSize] || 512;
            const dlQr = new QRCodeStyling(this.getQrOptions(size, fmt === 'svg' ? 'svg' : 'canvas', 10));
            dlQr.download({ name: 'qrcode', extension: fmt });
        },
    },

    computed: {
        qrCornerColor() {
            const swatch = COLOR_SWATCHES.find(s => s.value === this.qrColor);
            return swatch ? swatch.dark : this.qrColor;
        },

        isPresetColor() {
            return COLOR_SWATCHES.some(s => s.value === this.qrColor);
        },

        embedCode() {
            const sizeValue = this.selectedSize === 'small' ? 130 : this.selectedSize === 'medium' ? 200 : 260;
            return `<img height="${sizeValue}" width="${sizeValue}" alt="QR Code" src="${this.qrPreviewSrc}" >`;
        },

        qrCodeDimensions() {
            if (this.selectedSize === 'small')  return 'qr-styling-small';
            if (this.selectedSize === 'medium') return 'qr-styling-medium';
            return 'qr-styling-large';
        },

        qrCodeLink() {
            const url = this.useSettingsStoreObj().current_user.url;
            if (!url) return null;
            return `${APPLICATION_URL}/${url}`;
        },
    },

    created() {
        this.$watch(
            () => this.useSettingsStoreObj().init_data_loaded,
            (loaded) => {
                if (loaded) {
                    this.$nextTick(() => { this.generateQRCode(); });
                }
            },
            { immediate: true }
        );
    }
};
</script>
